import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { tsGetSampleRun, tsWhoAmI } from '../../Typescript/API_Response'
import { AxiosResponse } from 'axios'
import { useUserContext } from '../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useParams } from 'react-router-dom'
import PhpAPI from '../PHP/APIs/PhpAPI'

export async function getUAI(whoAmI: tsWhoAmI, sampleRun: tsGetSampleRun) {
  return PhpAPI.get(
    whoAmI!,
    `/stuart/screen/getHoldingUAI.php?HoldingID=${sampleRun.runInfo.OriginHoldingID}`,
  )
}

export function useGetScreenHoldings(facility_id: string, status_list: string) {
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  return useQuery<any, AxiosResponse>(
    ['ScreenService', 'getScreenHoldings', facility_id, status_list],
    () =>
      PhpAPI.get(
        whoAmI!,
        `/stuart/screen/getScreenHoldings.php?FacilityID=${facility_id}&CollectionStatus=${status_list}`,
      ),
    {
      enabled: !!whoAmI && !!facility_id && !!status_list,
      onError: (data) => setError(data),
      select: (data) => data.ScreenHoldings,
    },
  )
}

export function useGetScreenSampleStatus(
  facility_id: string,
  status_list: string,
) {
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  return useQuery<any, AxiosResponse>(
    ['ScreenService', 'getScreenSampleStatus', facility_id, status_list],
    () =>
      PhpAPI.get(
        whoAmI!,
        `/stuart/screen/getScreenSampleStatus.php?FacilityID=${facility_id}&CollectionStatus=${status_list}`,
      ),
    {
      enabled: !!whoAmI && !!facility_id && !!status_list,
      onError: (data) => setError(data),
      select: (data) => data.Screens,
    },
  )
}

export function useGetScreenReportResults(
  manifest: boolean,
  ids: string[],
  setIds: any,
) {
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  const key = manifest ? 'VesselIDs' : 'ScreenIDs'
  return useQueries(
    ids.map((id) => {
      return {
        queryKey: [
          'PhpAPI',
          '/stuart',
          '/report/sampleManifestReport.php',
          manifest,
          id,
        ],
        queryFn: () =>
          PhpAPI.post(whoAmI, `/stuart/report/sampleManifestReport.php`, {
            [key]: [id],
          }),
        onSuccess: (data: any) => {
          if (!data?.samples) {
            setIds((ids: any[]) => ids.filter((k) => k !== id))
          }
        },
        onError: (err: AxiosResponse) => setError(err),
      }
    }),
  )
}

export function useGetResultsReportData(ids: string[], setIds: any) {
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  return useQueries(
    ids.map((id) => {
      return {
        queryKey: ['PhpAPI', '/stuart', '/report/screenResultsReport.php', id],
        queryFn: () =>
          PhpAPI.post(whoAmI, `/stuart/report/screenResultsReport.php`, {
            ScreenIDs: [id],
          }),
        onSuccess: (data: any) => {
          if (!data?.samples) {
            setIds((ids: any[]) => ids.filter((k) => k !== id))
          }
        },
        onError: (err: AxiosResponse) => setError(err),
      }
    }),
  )
}

export function useUpdateScreenStatus() {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { screen_id } = useParams()
  const queryClient = useQueryClient()
  return useMutation<
    any,
    AxiosResponse,
    { billing_status: string; collection_status: string },
    () => void
  >(
    ({ billing_status, collection_status }) =>
      PhpAPI.get(
        whoAmI!,
        `/stuart/screen/updateScreen.php?ScreenID=${screen_id}&CollectionStatus=${collection_status}&BillingStatus=${billing_status}`,
      ),
    {
      onError: (data) => setError(data),
      onSuccess: async (data) => {
        if (data?.message) {
          setMessage({
            subject: 'Error updating screen',
            message: (data as any)?.message,
            severity: 'warning',
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ['PhpAPI', '/stuart', '/screen/getScreen.php'],
          })
        }
      },
    },
  )
}
